import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { ClipboardCheck, Plus, Check, Clock, CalendarDays, Timer, Trash2, Pencil, RotateCcw } from "lucide-react";
import TaskFormDialog from "@/components/ops/TaskFormDialog";
import CompleteTaskDialog from "@/components/ops/CompleteTaskDialog";
import PostponeTaskDialog from "@/components/ops/PostponeTaskDialog";
import PathBackdrop from "@/components/career/PathBackdrop";
import { statusOf, daysDelta, formatCountdown, formatDate } from "@/lib/crm";

const DOT_COLOR = { red: "bg-rose-500", yellow: "bg-amber-400", green: "bg-emerald-500", gray: "bg-stone-600" };

const STATUS_STYLE = {
  todo:        "border-stone-700 text-stone-300",
  in_progress: "border-amber-500/60 text-amber-300",
  done:        "border-emerald-500/60 text-emerald-400",
  postponed:   "border-blue-500/60 text-blue-300",
};

const CAT_KEYS = ["contact", "followup", "selftape", "materials", "networking", "research", "meeting", "other"];

function TaskRow({ t, task, contact, onEdit, onComplete, onPostpone, onDelete, onToggleProgress, lang }) {
  const dot = task.status === "done" ? "gray"
    : task.due ? statusOf(task.due).color : "gray";
  const dotGlow = {
    red: "shadow-[0_0_8px_rgba(244,63,94,0.6)]",
    yellow: "shadow-[0_0_8px_rgba(251,191,36,0.6)]",
    green: "shadow-[0_0_8px_rgba(16,185,129,0.6)]",
    gray: "",
  };
  return (
    <li
      data-testid={`task-${task.task_id}`}
      className="rounded-lg border border-stone-800/60 bg-stone-900/40 backdrop-blur-md p-6 md:p-10 transition-all hover:border-amber-500/50 hover:bg-stone-900/55"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
        {/* LEFT — subtle LED + title + tags + notes */}
        <div className="flex-1 flex items-start gap-4 min-w-0">
          <span
            className={`mt-3 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT_COLOR[dot]} ${dotGlow[dot]}`}
            data-testid={`task-dot-${task.task_id}`}
            aria-label={dot}
          />
          <div className="min-w-0 flex-1">
            <div className="serif text-2xl md:text-3xl lg:text-4xl text-stone-100 leading-tight">
              {task.title}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap text-sm">
              <span className="mono text-xs uppercase tracking-widest text-amber-500">
                {t(`ops.cat.${task.category}`)}
              </span>
              <span className={`mono text-xs uppercase tracking-widest border px-2.5 py-1 rounded-sm ${STATUS_STYLE[task.status]}`}>
                {t(`ops.status.${task.status}`)}
              </span>
              {contact && (
                <span className="text-stone-300 text-sm md:text-base">· {contact.first_name} {contact.last_name}</span>
              )}
            </div>
            {task.notes && (
              <div className="text-sm md:text-base text-stone-400 mt-3 whitespace-pre-line line-clamp-4">
                {task.notes}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — TIME + DATE with icons + Postpone (equal visual weight) */}
        <div className="flex flex-col items-start lg:items-end gap-3 lg:min-w-[300px]">
          {task.due ? (
            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="flex items-center gap-2 text-stone-300 text-lg md:text-xl">
                <Timer size={20} className="text-amber-500" strokeWidth={2.5} />
                <span className="mono text-sm md:text-base uppercase tracking-widest">
                  {formatCountdown(task.due, t)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <CalendarDays size={26} className="text-amber-500" strokeWidth={2.5} />
                <div className="text-right">
                  <div className="serif text-2xl md:text-3xl lg:text-4xl text-stone-100 leading-tight">
                    {formatDate(task.due, lang)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mono text-sm text-stone-500 uppercase tracking-widest">
              <CalendarDays size={18} /> —
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2 justify-start lg:justify-end">
            {task.status !== "done" && task.status !== "postponed" && (
              <button
                onClick={() => onPostpone(task)}
                data-testid={`task-postpone-${task.task_id}`}
                className="ghost-btn !py-3 !px-6 text-base md:text-lg border-2 hover:!border-amber-500/70 hover:!text-amber-300"
              >
                <Clock size={16} /> {t("ops.action.postpone")}
              </button>
            )}
            {task.status !== "done" && (
              <button
                onClick={() => onComplete(task)}
                data-testid={`task-complete-${task.task_id}`}
                className="pill-btn !py-3 !px-6 text-base md:text-lg"
              >
                <Check size={16} /> {t("ops.action.complete")}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1">
            {task.status === "todo" && (
              <button
                onClick={() => onToggleProgress(task, "in_progress")}
                data-testid={`task-inprog-${task.task_id}`}
                className="ghost-btn !py-1.5 !px-2 text-xs"
                title={t("ops.status.in_progress")}
              >
                <RotateCcw size={12} />
              </button>
            )}
            <button
              onClick={() => onEdit(task)}
              data-testid={`task-edit-${task.task_id}`}
              className="ghost-btn !py-1.5 !px-2 text-xs"
            >
              <Pencil size={12} />
            </button>
            <button
              onClick={() => onDelete(task)}
              data-testid={`task-delete-${task.task_id}`}
              className="ghost-btn !py-1.5 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>

      {task.postpone_count >= 2 && task.status === "postponed" && (
        <div className="mt-5 p-4 text-sm italic text-amber-200 bg-amber-500/5 border border-amber-500/30 rounded-sm">
          {t("ops.postpone.repeated_note")}
        </div>
      )}
    </li>
  );
}

export default function OperationalTasks() {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completeTask, setCompleteTask] = useState(null);
  const [postponeTask, setPostponeTask] = useState(null);

  const load = async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        api.get(`/ops/tasks?lang=${encodeURIComponent(lang)}`),
        api.get("/crm/contacts"),
      ]);
      setTasks(tRes.data || []);
      setContacts(cRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const contactById = (id) => contacts.find((c) => c.contact_id === id) || null;

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (task) => { setEditing(task); setFormOpen(true); };

  const del = async (task) => {
    if (!window.confirm(t("ops.confirm_delete"))) return;
    try {
      await api.delete(`/ops/tasks/${task.task_id}`);
      setTasks((prev) => prev.filter((x) => x.task_id !== task.task_id));
      toast.success(t("ops.deleted"));
    } catch { toast.error(t("ops.form.fail")); }
  };

  const toggleProgress = async (task, status) => {
    try {
      const r = await api.patch(`/ops/tasks/${task.task_id}`, { status });
      setTasks((prev) => prev.map((x) => x.task_id === task.task_id ? r.data : x));
    } catch { toast.error(t("ops.form.fail")); }
  };

  const onCompleteClick = (task) => {
    if (task.contact_id) setCompleteTask(task);
    else {
      // simple complete
      (async () => {
        try {
          const r = await api.post(`/ops/tasks/${task.task_id}/complete`, {});
          setTasks((prev) => prev.map((x) => x.task_id === task.task_id ? r.data.task : x));
          toast.success(t("ops.complete.simple"));
        } catch { toast.error(t("ops.form.fail")); }
      })();
    }
  };

  const afterMutation = async () => { await load(); };

  const filtered = filter === "all" ? tasks
    : filter === "open" ? tasks.filter((x) => x.status === "todo" || x.status === "in_progress")
    : tasks.filter((x) => x.status === filter);

  // Chronologically ordered sections — used only when we're showing open tasks.
  const showSections = filter === "all" || filter === "open";
  const sortByDue = (a, b) => {
    const da = a.due ? new Date(a.due) : new Date("2999-01-01");
    const dbb = b.due ? new Date(b.due) : new Date("2999-01-01");
    return da - dbb;
  };
  const openTasks = tasks.filter((x) => ["todo", "in_progress", "postponed"].includes(x.status));
  const doneTasks = tasks.filter((x) => x.status === "done").sort((a, b) =>
    new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  const secToday = [], secUpcoming = [], secLater = [];
  for (const tk of openTasks) {
    const d = tk.due ? daysDelta(tk.due) : null;
    if (d === null) secLater.push(tk);
    else if (d <= 0) secToday.push(tk);
    else if (d <= 7) secUpcoming.push(tk);
    else secLater.push(tk);
  }
  secToday.sort(sortByDue); secUpcoming.sort(sortByDue); secLater.sort(sortByDue);

  return (
    <div className="space-y-6" data-testid="ops-tasks-page">
      <PathBackdrop />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
            <ClipboardCheck size={12} /> {t("ops.title")}
          </div>
          <h1 className="serif text-5xl text-stone-100 mt-2">{t("ops.title")}</h1>
          <p className="text-stone-400 mt-2 max-w-xl text-sm">{t("ops.subtitle")}</p>
        </div>
        <button data-testid="task-new-btn" onClick={openNew} className="pill-btn">
          <Plus size={16} /> {t("ops.new")}
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1 flex-wrap border-b border-stone-900 pb-2">
        {["all", "open", "todo", "in_progress", "done", "postponed"].map((k) => (
          <button
            key={k}
            data-testid={`task-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`ghost-btn !py-1.5 !px-3 text-xs ${filter === k ? "!border-amber-500/60 !text-amber-400" : ""}`}
          >
            {k === "all" ? t("crm.tab.all")
             : k === "open" ? t("ops.status.todo") + " + " + t("ops.status.in_progress")
             : t(`ops.status.${k}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>
      ) : tasks.length === 0 ? (
        <div className="paper p-10 text-center">
          <ClipboardCheck className="mx-auto text-stone-500" />
          <div className="serif text-2xl text-stone-200 mt-3">{t("ops.empty")}</div>
          <button onClick={openNew} className="pill-btn mt-6" data-testid="task-empty-new">
            <Plus size={14} /> {t("ops.new")}
          </button>
        </div>
      ) : showSections ? (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          {[
            { key: "today",    title: t("ops.sec.today"),    dot: "red",    items: secToday,    glow: "shadow-[0_0_8px_rgba(244,63,94,0.5)]" },
            { key: "upcoming", title: t("ops.sec.upcoming"), dot: "yellow", items: secUpcoming, glow: "shadow-[0_0_8px_rgba(251,191,36,0.5)]" },
            { key: "later",    title: t("ops.sec.later"),    dot: "green",  items: secLater,    glow: "shadow-[0_0_8px_rgba(16,185,129,0.5)]" },
          ].map((sec) => (
            <section key={sec.key} data-testid={`ops-sec-${sec.key}`}>
              <div className="flex items-center gap-2 mb-4">
                <span className={`inline-block w-2 h-2 rounded-full ${DOT_COLOR[sec.dot]} ${sec.glow}`} />
                <h3 className="mono text-[11px] tracking-[0.3em] text-stone-300 uppercase">
                  {sec.title}
                </h3>
                <span className="mono text-[10px] text-stone-500">· {sec.items.length}</span>
              </div>
              {sec.items.length === 0 ? (
                <div className="text-xs text-stone-500 italic">{t("ops.sec.empty")}</div>
              ) : (
                <ul className="space-y-3">
                  {sec.items.map((task) => (
                    <TaskRow
                      key={task.task_id} t={t} lang={lang} task={task}
                      contact={contactById(task.contact_id)}
                      onEdit={openEdit} onComplete={onCompleteClick}
                      onPostpone={setPostponeTask} onDelete={del}
                      onToggleProgress={toggleProgress}
                    />
                  ))}
                </ul>
              )}
            </section>
          ))}
          {filter === "all" && doneTasks.length > 0 && (
            <section data-testid="ops-sec-done">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-block w-2 h-2 rounded-full bg-stone-600" />
                <h3 className="mono text-[11px] tracking-[0.3em] text-stone-300 uppercase">
                  {t("ops.sec.done")}
                </h3>
                <span className="mono text-[10px] text-stone-500">· {doneTasks.length}</span>
              </div>
              <ul className="space-y-3">
                {doneTasks.slice(0, 10).map((task) => (
                  <TaskRow
                    key={task.task_id} t={t} lang={lang} task={task}
                    contact={contactById(task.contact_id)}
                    onEdit={openEdit} onComplete={onCompleteClick}
                    onPostpone={setPostponeTask} onDelete={del}
                    onToggleProgress={toggleProgress}
                  />
                ))}
              </ul>
            </section>
          )}
        </motion.div>
      ) : filtered.length === 0 ? (
        <div className="paper p-10 text-center">
          <ClipboardCheck className="mx-auto text-stone-500" />
          <div className="serif text-2xl text-stone-200 mt-3">{t("ops.empty")}</div>
        </div>
      ) : (
        <motion.ul
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {filtered.map((task) => (
            <TaskRow
              key={task.task_id} t={t} lang={lang} task={task}
              contact={contactById(task.contact_id)}
              onEdit={openEdit} onComplete={onCompleteClick}
              onPostpone={setPostponeTask} onDelete={del}
              onToggleProgress={toggleProgress}
            />
          ))}
        </motion.ul>
      )}

      <TaskFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        contacts={contacts}
        task={editing}
        onSaved={afterMutation}
      />
      <CompleteTaskDialog
        task={completeTask}
        contact={completeTask ? contactById(completeTask.contact_id) : null}
        onClose={() => setCompleteTask(null)}
        onDone={afterMutation}
      />
      <PostponeTaskDialog
        task={postponeTask}
        onClose={() => setPostponeTask(null)}
        onDone={afterMutation}
      />
    </div>
  );
}
